// Route table: path pattern → page and the roles that may open it (SPEC §7, §9.4).

import type { Route } from "./router";
import { navigate } from "./router";
import { requireUser } from "./session";
import { renderChangePasswordPage } from "./pages/account/changePasswordPage";
import { renderDayPage } from "./pages/day/dayPage";
import { renderLessonPage } from "./pages/lesson/lessonPage";
import { renderGroupsListPage } from "./pages/groups/groupsListPage";
import { renderGroupCreatePage } from "./pages/groups/groupCreatePage";
import { renderGroupPage } from "./pages/groups/groupPage";
import { renderTeachersListPage } from "./pages/teachers/teachersListPage";
import { renderTeacherCreatePage } from "./pages/teachers/teacherCreatePage";
import { renderTeacherPage } from "./pages/teachers/teacherPage";
import { renderChangesPage } from "./pages/changes/changesPage";
import { renderCentersPage } from "./pages/admin/centersPage";
import { renderCenterPage } from "./pages/admin/centerPage";
import { renderTravelTimesPage } from "./pages/admin/travelTimesPage";
import { renderClassroomPage } from "./pages/admin/classroomPage";
import { renderCoursesPage } from "./pages/admin/coursesPage";
import { renderUsersPage } from "./pages/admin/usersPage";
import { renderMySchedulePage } from "./pages/mySchedule/mySchedulePage";
import { renderNotificationsPage } from "./pages/notifications/notificationsPage";

const everyone: Route["roles"] = ["Admin", "Viewer", "Teacher"];
const office: Route["roles"] = ["Admin", "Viewer"];

/** Every screen of the app. */
export const routes: Route[] = [
  { pattern: "/", roles: everyone, render: renderHome },
  { pattern: "/account/password", roles: everyone, render: renderChangePasswordPage },
  { pattern: "/day", roles: office, render: renderDayPage },
  { pattern: "/lessons/:groupId/:date/:slotId", roles: office, render: renderLessonPage },
  { pattern: "/groups", roles: office, render: renderGroupsListPage },
  { pattern: "/groups/new", roles: ["Admin"], render: renderGroupCreatePage },
  { pattern: "/groups/:id", roles: office, render: renderGroupPage },
  { pattern: "/teachers", roles: office, render: renderTeachersListPage },
  { pattern: "/teachers/new", roles: ["Admin"], render: renderTeacherCreatePage },
  { pattern: "/teachers/:id", roles: office, render: renderTeacherPage },
  { pattern: "/changes", roles: office, render: renderChangesPage },
  { pattern: "/admin/centers", roles: office, render: renderCentersPage },
  { pattern: "/admin/centers/:id", roles: office, render: renderCenterPage },
  { pattern: "/admin/travel", roles: office, render: renderTravelTimesPage },
  { pattern: "/admin/classrooms/:id", roles: office, render: renderClassroomPage },
  { pattern: "/admin/courses", roles: office, render: renderCoursesPage },
  { pattern: "/admin/users", roles: ["Admin"], render: renderUsersPage },
  { pattern: "/my-schedule", roles: ["Teacher"], render: renderMySchedulePage },
  { pattern: "/notifications", roles: everyone, render: renderNotificationsPage },
];

/** "/" goes to the role's home: Day view for admins and viewers, My schedule for teachers (SPEC §7.1). */
async function renderHome(): Promise<void> {
  navigate(homePath());
  return Promise.resolve();
}

/** The home screen path of the signed-in user. */
export function homePath(): string {
  return requireUser().role === "Teacher" ? "/my-schedule" : "/day";
}
